package com.tfc.care.tfccare.utils

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.graphics.Color
import com.tfc.care.tfccare.ui.BaseViewModel
import com.tfc.care.tfccare.ui.StaffChatViewModel
import com.tfc.care.tfccare.ui.theme.backgroundDark
import org.koin.mp.KoinPlatform.getKoin
import kotlin.js.Date


/**
 * Returns the current time in milliseconds as a string.
 *
 * @return A string representing the current epoch time in milliseconds.
 */


/**
 * Returns the current date in ISO format (YYYY-MM-DD).
 *
 * @return A string representing the current date.
 */
fun getCurrentDate(): String {
    val now = Date()
    return now.toISOString().substring(0, 10)
}

/**
 * Retrieves the current date and time as a string in ISO format.
 *
 * @return A string representing the current date and time in ISO 8601 format.
 * Example: "2026-02-17T12:35:00.000Z"
 */
fun getCurrentDateAndTime(): String {
    val now = Date()
    return now.toISOString()
}
/**
 * Returns the current year and month in YYYY-MM format.
 *
 * @return A string representing the current month.
 */
fun getCurrentMonth(): String {
    val now = Date()
    val year = now.getFullYear()
    val month = (now.getMonth() + 1).toString().padStart(2, '0')
    return "$year-$month"
}

/**
 * Returns the previous month in YYYY-MM format.
 * If the current month is January, it returns December of the previous year.
 *
 * @return A string representing the previous month.
 */
fun getPreviousMonth(): String {
    val now = Date()
    var year = now.getFullYear()
    var month = now.getMonth() // 0-indexed
    
    if (month == 0) {
        month = 12
        year -= 1
    }
    
    return "$year-${month.toString().padStart(2, '0')}"
}


/**
 * Extension function for [String] that converts a millisecond timestamp string 
 * into a "time ago" format (e.g., "5m ago", "2h ago").
 * @return A human-readable string representing how much time has passed since the timestamp.
 */
fun String.toTimeAgo(): String {
    val time = try {
        if (this.contains("T") && this.endsWith("Z")) {
            Date(this).getTime().toLong()
        } else {
            this.toLong()
        }
    } catch (e: Exception) {
        0L
    }
    if (time == 0L) return this
    
    val now = Date.now().toLong()
    val diffMillis = now - time

    val minute = 60_000L
    val hour = 60 * minute
    val day = 24 * hour

    return when {
        diffMillis < minute ->
            "Just now"

        diffMillis < hour ->
            "${diffMillis / minute}m ago"

        diffMillis < day ->
            "${diffMillis / hour}h ago"

        else ->
            "${diffMillis / day}d ago"
    }
}

fun String.toConversationTime(): String {
    if (this.isEmpty()) return ""
    val time = try {
        if (this.contains("T") && this.endsWith("Z")) {
            Date(this).getTime().toLong()
        } else {
            this.toLong()
        }
    } catch (e: Exception) {
        0L
    }
    if (time == 0L) return this

    val now = Date()
    val nowDate = Date(now.getFullYear(), now.getMonth(), now.getDate())
    val msgDate = Date(time.toDouble())
    val msgDateOnly = Date(msgDate.getFullYear(), msgDate.getMonth(), msgDate.getDate())

    val diffMillis = now.getTime().toLong() - time
    val minute = 60_000L
    val hour = 60 * minute

    return when {
        diffMillis < hour -> {
            if (diffMillis < minute) "Just now"
            else "${diffMillis / minute} min ago"
        }
        nowDate.getTime() == msgDateOnly.getTime() -> {
            formatTime(this)
        }
        else -> {
            val day = msgDate.getDate().toString().padStart(2, '0')
            val month = (msgDate.getMonth() + 1).toString().padStart(2, '0')
            val year = msgDate.getFullYear()
            "$day/$month/$year"
        }
    }
}

/**
 * Formats a timestamp to a header string like "Today", "Yesterday" or a date.
 */
fun String.toDateHeader(): String {
    if (this.isEmpty()) return ""
    val time = try {
        if (this.contains("T") && this.endsWith("Z")) {
            Date(this).getTime().toLong()
        } else {
            this.toLong()
        }
    } catch (e: Exception) {
        0L
    }
    if (time == 0L) return this

    val now = Date()
    val today = Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime().toLong()
    val yesterday = today - 24 * 60 * 60 * 1000L
    
    val msgDate = Date(time.toDouble())
    val msgDateOnly = Date(msgDate.getFullYear(), msgDate.getMonth(), msgDate.getDate()).getTime().toLong()

    return when (msgDateOnly) {
        today -> "Today"
        yesterday -> "Yesterday"
        else -> {
            val day = msgDate.getDate().toString().padStart(2, '0')
            val month = (msgDate.getMonth() + 1).toString().padStart(2, '0')
            val year = msgDate.getFullYear()
            "$day/$month/$year"
        }
    }
}

/**
 * Extension function for [String] that checks if it is a valid email address.
 *
 * @return `true` if the string matches the email regex, `false` otherwise.
 */
fun String.isValidEmail(): Boolean {
    return this.matches(Regex("[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}"))
}


/**
 * Formats a millisecond timestamp string into a readable time string (HH:MM AM/PM).
 *
 * @param timestamp The millisecond timestamp as a string.
 * @return A formatted time string.
 */
fun formatTime(timestamp: String): String {
    val d = if (timestamp.contains("T") && timestamp.endsWith("Z")) {
        Date(timestamp)
    } else {
        Date(timestamp.toDouble())
    }

    var hour = d.getHours()
    val minute = d.getMinutes()
    val ampm = if (hour >= 12) "PM" else "AM"

    hour %= 12
    if (hour == 0) hour = 12

    return "${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} $ampm"
}


/**
 * A Composable function that retrieves and remembers the [StaffChatViewModel] 
 * from Koin and ensures it is cleared when the Composable leaves the composition.
 *
 * @return The [StaffChatViewModel] instance.
 */
@Composable
fun rememberViewModel(): StaffChatViewModel {
    val vm = remember { getKoin().get<StaffChatViewModel>() }

    DisposableEffect(Unit) {
        onDispose { (vm as? BaseViewModel)?.clear() }
    }

    return vm
}



fun Any?.toBooleanSafe(default: Boolean = false): Boolean {
    return when (this) {
        is Boolean -> this
        is Int -> this == 1
        is Long -> this == 1L
        is Double -> this == 1.0
        is String -> this.equals("true", true) || this == "1"
        else -> default
    }
}



@Composable
fun isDarkTheme(color: Color):Boolean{
    return color == backgroundDark
}